/**
 * React Query Hooks for KuCoin Exchange API
 *
 * These hooks provide:
 * - Automatic refetching
 * - Caching
 * - Loading/error states
 * - Retry logic
 */

import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import {
  kucoinAPI,
  TradingBot,
  EarnPosition,
  EarnProduct,
  VIPTier,
  KuCoinStats,
} from '../lib/api/kucoin-api';

/**
 * Hook to fetch trading bots from KuCoin
 *
 * Auto-refreshes every 30 seconds when tab is active
 *
 * ⚠️ Note: KuCoin doesn't have native bot endpoint
 * Using custom tracking via orders + backend DB
 */
export function useTradingBots(options?: UseQueryOptions<TradingBot[], Error>) {
  return useQuery<TradingBot[], Error>({
    queryKey: ['kucoin', 'trading-bots'],
    queryFn: () => kucoinAPI.fetchTradingBots(),
    staleTime: 30 * 1000, // Consider data stale after 30s
    refetchInterval: 30 * 1000, // Auto-refresh every 30s
    refetchOnWindowFocus: true,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    ...options,
  });
}

/**
 * Hook to fetch KuCoin Earn positions
 *
 * Auto-refreshes every 60 seconds (Earn data changes less frequently)
 */
export function useEarnPositions(options?: UseQueryOptions<EarnPosition[], Error>) {
  return useQuery<EarnPosition[], Error>({
    queryKey: ['kucoin', 'earn-positions'],
    queryFn: () => kucoinAPI.fetchEarnPositions(),
    staleTime: 60 * 1000, // Consider data stale after 60s
    refetchInterval: 60 * 1000, // Auto-refresh every 60s
    refetchOnWindowFocus: true,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    ...options,
  });
}

/**
 * Hook to fetch KuCoin Earn products (available for subscription)
 *
 * Auto-refreshes every 5 minutes (product data changes rarely)
 */
export function useEarnProducts(options?: UseQueryOptions<EarnProduct[], Error>) {
  return useQuery<EarnProduct[], Error>({
    queryKey: ['kucoin', 'earn-products'],
    queryFn: () => kucoinAPI.fetchEarnProducts(),
    staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
    refetchInterval: 5 * 60 * 1000, // Auto-refresh every 5 minutes
    refetchOnWindowFocus: true,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    ...options,
  });
}

/**
 * Hook to fetch VIP Tier information
 *
 * Auto-refreshes every 60 seconds
 */
export function useVIPTier(options?: UseQueryOptions<VIPTier, Error>) {
  return useQuery<VIPTier, Error>({
    queryKey: ['kucoin', 'vip-tier'],
    queryFn: () => kucoinAPI.fetchVIPTier(),
    staleTime: 60 * 1000, // Consider data stale after 60s
    refetchInterval: 60 * 1000, // Auto-refresh every 60s
    refetchOnWindowFocus: true,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    ...options,
  });
}

/**
 * Hook to fetch account summary (KPIs)
 *
 * Auto-refreshes every 30 seconds
 */
export function useAccountSummary(options?: UseQueryOptions<KuCoinStats, Error>) {
  return useQuery<KuCoinStats, Error>({
    queryKey: ['kucoin', 'account-summary'],
    queryFn: () => kucoinAPI.fetchAccountSummary(),
    staleTime: 30 * 1000, // Consider data stale after 30s
    refetchInterval: 30 * 1000, // Auto-refresh every 30s
    refetchOnWindowFocus: true,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    ...options,
  });
}

/**
 * Helper hook to get combined stats from all data sources
 *
 * This aggregates data from multiple endpoints for dashboard KPIs
 */
export function useKucoinStats() {
  const { data: bots = [], isLoading: botsLoading } = useTradingBots();
  const { data: earnPositions = [], isLoading: earnLoading } = useEarnPositions();
  const { data: vipTier, isLoading: vipLoading } = useVIPTier();
  const { data: summary, isLoading: summaryLoading } = useAccountSummary();

  const totalTradingProfit = bots.reduce((sum, bot) => sum + bot.stats.profit, 0);
  const totalEarnRewards = earnPositions.reduce(
    (sum, pos) => sum + pos.totalRewards,
    0
  );
  const totalEarnValue = earnPositions.reduce((sum, pos) => sum + pos.amount, 0);
  const activeBots = bots.filter((bot) => bot.status === 'ACTIVE').length;
  const avgEarnAPY =
    earnPositions.length > 0
      ? earnPositions.reduce((sum, pos) => sum + pos.apy, 0) / earnPositions.length
      : 0;

  return {
    // Trading
    totalTradingProfit,
    activeBots,
    totalBots: bots.length,

    // Earn
    totalEarnRewards,
    totalEarnValue,
    avgEarnAPY,
    totalEarnPositions: earnPositions.length,

    // VIP
    vipTier,

    // Summary (from API)
    totalEquity: summary?.totalEquity || 0,
    totalSpot: summary?.totalSpot || 0,
    tradingVolume24h: summary?.tradingVolume24h || 0,
    tradingFeePaid24h: summary?.tradingFeePaid24h || 0,

    // Loading states
    isLoading: botsLoading || earnLoading || vipLoading || summaryLoading,
  };
}
